/**
 * `useMediaQuery` — reactive CSS media query hook.
 *
 * Returns `true` when the query currently matches, `false` otherwise.
 * Subscribes to changes and triggers re-renders when match state changes.
 *
 * SSR/jsdom-safe: `window.matchMedia` may not exist. In those environments
 * the hook returns `false` (non-matching). Tests that need a specific value
 * should mock `window.matchMedia` before rendering.
 *
 * Usage:
 *   const isDesktop = useMediaQuery('(min-width: 1024px)');
 */
import { useState, useEffect } from "react";

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    setMatches(mql.matches);

    const handler = (e: MediaQueryListEvent): void => {
      setMatches(e.matches);
    };

    // `addEventListener` is the modern API; fall back to deprecated
    // `addListener` for older Safari (< 14).
    if (mql.addEventListener) {
      mql.addEventListener("change", handler);
      return () => mql.removeEventListener("change", handler);
    } else {
      mql.addListener(handler);
      return () => mql.removeListener(handler);
    }
  }, [query]);

  return matches;
}
